import { Fragment, ReactElement, useMemo } from 'react';
import { DatePicker } from '@Components/DatePicker/DatePicker';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { Dropdown } from '@Components/Dropdown';
import { Placement, Size } from '@Components/designConstants';
import {
  AggregatedRow,
  DIMENSIONS,
  Metric,
  METRICS,
  MetricKey
} from './streaming-media-planner-utils';

interface TableViewTabProps {
  range: DateRange;
  onRangeChange: (range: DateRange) => void;
  selectedDimensions: string[];
  onDimensionsChange: (values: string[]) => void;
  selectedMetrics: MetricKey[];
  onMetricsChange: (values: MetricKey[]) => void;
  aggregated: AggregatedRow[];
}

const DIMENSION_OPTIONS = DIMENSIONS.map(d => ({ label: d.label, value: d.key }));
const METRIC_OPTIONS = METRICS.map(m => ({ label: m.label, value: m.key }));

const formatDollar = (n: number, decimals = 0): string => {
  if (!Number.isFinite(n) || n === 0) return '$0';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

const formatInteger = (n: number): string =>
  Number.isFinite(n) && n > 0 ? Math.round(n).toLocaleString() : '0';

const formatMetric = (key: MetricKey, value: number): string => {
  switch (key) {
    case Metric.IMPRESSIONS:
      return formatInteger(value);
    case Metric.AVG_CPM:
      return formatDollar(value, 2);
    case Metric.INVESTMENT:
    case Metric.BPM_AD_SERVING:
    case Metric.BPM_FEE:
    case Metric.GRAND_TOTAL:
    case Metric.ESTIMATED_SPEND:
      return formatDollar(value, 2);
    default:
      return String(value);
  }
};

export const TableViewTab = ({
  range,
  onRangeChange,
  selectedDimensions,
  onDimensionsChange,
  selectedMetrics,
  onMetricsChange,
  aggregated
}: TableViewTabProps): ReactElement => {
  const sortedRows = useMemo(() => {
    const primaryMetric = selectedMetrics[0];
    if (!primaryMetric) return aggregated;
    return [...aggregated].sort((a, b) => b.totals[primaryMetric] - a.totals[primaryMetric]);
  }, [aggregated, selectedMetrics]);

  const totals = useMemo(() => {
    const acc: Record<MetricKey, number> = {
      [Metric.INVESTMENT]: 0,
      [Metric.IMPRESSIONS]: 0,
      [Metric.AVG_CPM]: 0,
      [Metric.BPM_AD_SERVING]: 0,
      [Metric.BPM_FEE]: 0,
      [Metric.GRAND_TOTAL]: 0,
      [Metric.ESTIMATED_SPEND]: 0
    };
    for (const row of aggregated) {
      acc[Metric.INVESTMENT] += row.totals[Metric.INVESTMENT];
      acc[Metric.IMPRESSIONS] += row.totals[Metric.IMPRESSIONS];
      acc[Metric.BPM_AD_SERVING] += row.totals[Metric.BPM_AD_SERVING];
      acc[Metric.BPM_FEE] += row.totals[Metric.BPM_FEE];
      acc[Metric.GRAND_TOTAL] += row.totals[Metric.GRAND_TOTAL];
      acc[Metric.ESTIMATED_SPEND] += row.totals[Metric.ESTIMATED_SPEND];
    }
    acc[Metric.AVG_CPM] =
      acc[Metric.IMPRESSIONS] > 0 ? (acc[Metric.INVESTMENT] / acc[Metric.IMPRESSIONS]) * 1000 : 0;
    return acc;
  }, [aggregated]);

  const hasRange = Boolean(range.start) && Boolean(range.end);

  const renderBody = (): ReactElement => {
    if (!hasRange) {
      return <EmptyState message='Pick a date range to view aggregated planned spend.' />;
    }
    if (selectedDimensions.length === 0) {
      return <EmptyState message='Pick at least one dimension to group by.' />;
    }
    if (selectedMetrics.length === 0) {
      return <EmptyState message='Pick at least one metric to display.' />;
    }
    if (sortedRows.length === 0) {
      return <EmptyState message='No planned spend in the selected date range.' />;
    }
    return (
      <AggregatedTable
        rows={sortedRows}
        totals={totals}
        dimensionKeys={selectedDimensions}
        metricKeys={selectedMetrics}
      />
    );
  };

  return (
    <div className='table-view-tab'>
      <div className='table-view-controls'>
        <div className='control-group'>
          <span className='control-label'>Date Range</span>
          <DatePicker
            range={range}
            onChange={onRangeChange}
            isOutsideRange={() => false}
            size={Size.SM}
          />
        </div>
        <div className='control-group'>
          <span className='control-label'>Dimensions</span>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            multiSelect
            options={DIMENSION_OPTIONS}
            value={selectedDimensions}
            onChange={values => onDimensionsChange(values)}
            placeholder='Pick dimensions'
            size={Size.SM}
          />
        </div>
        <div className='control-group'>
          <span className='control-label'>Metrics</span>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            multiSelect
            options={METRIC_OPTIONS}
            value={selectedMetrics}
            onChange={values => onMetricsChange(values as MetricKey[])}
            placeholder='Pick metrics'
            size={Size.SM}
          />
        </div>
      </div>

      {renderBody()}
    </div>
  );
};

interface EmptyStateProps {
  message: string;
}

const EmptyState = ({ message }: EmptyStateProps): ReactElement => (
  <div className='table-view-empty'>{message}</div>
);

interface AggregatedTableProps {
  rows: AggregatedRow[];
  totals: Record<MetricKey, number>;
  dimensionKeys: string[];
  metricKeys: MetricKey[];
}

const AggregatedTable = ({
  rows,
  totals,
  dimensionKeys,
  metricKeys
}: AggregatedTableProps): ReactElement => {
  const dimensionLabel = (key: string): string => DIMENSIONS.find(d => d.key === key)?.label ?? key;
  const metricLabel = (key: MetricKey): string => METRICS.find(m => m.key === key)?.label ?? key;

  return (
    <table className='table-view-table'>
      <thead>
        <tr>
          {dimensionKeys.map(key => (
            <th key={key} className='dim-col'>
              {dimensionLabel(key)}
            </th>
          ))}
          {metricKeys.map(key => (
            <th key={key} className='metric-col'>
              {metricLabel(key)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri}>
            {dimensionKeys.map(key => (
              <td key={key} className='dim-col'>
                {row.dimensions[key]}
              </td>
            ))}
            {metricKeys.map(key => (
              <Fragment key={key}>
                <td className='metric-col'>{formatMetric(key, row.totals[key])}</td>
              </Fragment>
            ))}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className='totals-row'>
          <td colSpan={dimensionKeys.length} className='total-label'>
            Total
          </td>
          {metricKeys.map(key => (
            <td key={key} className='metric-col'>
              {formatMetric(key, totals[key])}
            </td>
          ))}
        </tr>
      </tfoot>
    </table>
  );
};
